import updateCoordinator from "./UpdateCoordinator.ts";
import type { DeferredUIOperation } from "./UpdateCoordinator.ts";
import { RenderInvalidationReason } from "./RenderInvalidation.ts";

export { type DeferredUIOperation };

export enum UpdateOrigin {
    Initial        = "initial",
    ChannelAdd     = "channel:add",
    ChannelUpdate  = "channel:update",
    ChannelDelete  = "channel:delete",
    ChannelClear   = "channel:clear",
    ChannelReset   = "channel:reset",
    ChannelSync    = "channel:sync",
    Reconnect      = "reconnect",
    DatasheetBuild = "datasheet:build",
    LayoutRebuild  = "layout:rebuild",
    RenderUpdate   = "render:update",
    Switch         = "switch",
}

export interface UpdateContextHandle {
    readonly id: string;
    readonly origin: UpdateOrigin;
    register(itemId: string): void;
    complete(itemId: string): void;
    defer(op: DeferredUIOperation): void;
    queueInvalidation(itemId: string, reason: RenderInvalidationReason, ids: Iterable<string>): void;
    readonly active: boolean;
    /* see docs/EXTRACTED_COMMENTS.md#c223 */
    sessionId?: number;
}

class UpdateContextHandleImpl implements UpdateContextHandle {
    public sessionId?: number;

    constructor(
        readonly id: string,
        readonly origin: UpdateOrigin,
    ) {}

    get active(): boolean {
        return updateCoordinator.isContextActive(this.id);
    }

    register(itemId: string): void {
        updateCoordinator.registerUpdate(this.id, itemId);
    }

    complete(itemId: string): void {
        updateCoordinator.completeUpdate(itemId, this.id);
    }

    defer(op: DeferredUIOperation): void {
        updateCoordinator.deferUIOperation(op.itemId, op);
    }

    queueInvalidation(itemId: string, reason: RenderInvalidationReason, ids: Iterable<string>): void {
        updateCoordinator.queueInvalidation(itemId, reason, ids);
    }
}

class UpdateContextRegistry {
    private nextId = 0;

    create(origin: UpdateOrigin, onComplete?: () => void): UpdateContextHandle {
        const id = `ctx:${origin}:${++this.nextId}:${Date.now()}`;
        updateCoordinator.createContext(id, null, onComplete);
        return new UpdateContextHandleImpl(id, origin);
    }

    seal(handle: UpdateContextHandle): void {
        updateCoordinator.sealRegistration(handle.id);
    }

    resolve(id: string): UpdateContextHandle | null {
        if (!updateCoordinator.isContextActive(id)) return null;
        return new UpdateContextHandleImpl(id, this.parseOrigin(id));
    }

    get hasActive(): boolean {
        return updateCoordinator.hasActiveContexts();
    }

    clear(): void {
        updateCoordinator.clear();
        this.nextId = 0;
    }

    private parseOrigin(id: string): UpdateOrigin {
        for (const origin of Object.values(UpdateOrigin)) {
            if (id.includes(origin)) return origin;
        }
        return UpdateOrigin.ChannelUpdate;
    }
}

export const updateContextRegistry = new UpdateContextRegistry();
